import type { CloseGuardReason, ToolHost } from '@slate/core';
import { useDocStore, type OpenDoc } from '../store/docStore';
import { ToolRegistry } from '../tools/ToolRegistry';

export interface CloseGuardResult {
  /** Docs with unsaved changes, in tab order. */
  dirtyDocs: OpenDoc[];
  /** Blocking reasons contributed by registered tools (e.g. busy terminals). */
  busyReasons: CloseGuardReason[];
}

/**
 * Aggregates everything that should give the user pause before closing: dirty
 * documents (read live from the doc store) and any blocking reasons contributed
 * by registered tools via `contributions.closeGuard.isBlocking`.
 *
 * Tool guards run concurrently and are individually fault-isolated — a guard
 * that throws or rejects contributes nothing and never aborts the close
 * evaluation (a misbehaving tool must not be able to trap the user in the app).
 */
export async function evaluateCloseGuard(host: ToolHost): Promise<CloseGuardResult> {
  const dirtyDocs = useDocStore.getState().docs.filter((d) => d.dirty);

  const reasonGroups = await Promise.all(
    ToolRegistry.getAll().map(async (def) => {
      const guard = def.contributions?.closeGuard;
      if (!guard) return [];
      try {
        return await guard.isBlocking(host);
      } catch {
        return [];
      }
    }),
  );

  return { dirtyDocs, busyReasons: reasonGroups.flat() };
}
